// src/pages/Notifications.js
// Admin broadcast tool — sends push + email to filtered user segments.
// Delivery handled by onAdminBroadcast Cloud Function.
// Supports attaching offer codes to messages (for Pro upgrade campaigns).
import React, { useState, useEffect } from "react";
import {
  collection, addDoc, getDocs, query,
  orderBy, serverTimestamp, limit,
} from "firebase/firestore";
import { db } from "../firebase";
import Paginator from "../components/Paginator";

const PAGE_SIZE = 10;

const TEMPLATES = [
  {
    label: "🎉 Launch Announcement",
    emailSubject: "Crewboard is live — here's what's new",
    title: "Crewboard is officially live! 🚛",
    body: "We've launched! Verified CDL drivers and hiring companies are now connecting on Crewboard. Update your profile to get hired faster.",
    target: "driver",
  },
  {
    label: "⭐ Pro Upgrade (with code)",
    emailSubject: "You're invited to try Crewboard Pro — free",
    title: "Try Crewboard Pro — on us",
    body: "Upgrade to Pro and unlock unlimited job posts, Search Drivers, saved pools, and priority support. Use your exclusive code at checkout.",
    target: "hirer",
    offerCodePrompt: true,
  },
  {
    label: "📋 Basic Limit Reminder",
    emailSubject: "You're almost at your monthly job post limit",
    title: "You're close to your monthly limit",
    body: "Basic plan includes 3 job posts per month. Upgrade to Pro for unlimited posts and full access to our driver pool.",
    target: "hirer",
  },
  {
    label: "🚛 New Jobs Available",
    emailSubject: "New CDL jobs just posted near you",
    title: "New jobs available near you!",
    body: "Hirers are actively posting CDL positions on Crewboard. Open the app and apply now before spots fill up.",
    target: "driver",
  },
  {
    label: "⚠️ Complete Your Profile",
    emailSubject: "Complete your Crewboard profile to get hired",
    title: "Your profile needs attention",
    body: "Drivers with complete profiles get hired 3x faster. Upload your CDL, add a photo, and write a bio to stand out.",
    target: "driver",
  },
  {
    label: "🏢 Re-engage Inactive Hirers",
    emailSubject: "Miss Crewboard? Your driver pool is waiting",
    title: "Verified drivers are waiting for you",
    body: "You haven't posted a job recently. Your driver pool is ready — post a job today and receive applications within hours.",
    target: "hirer",
  },
  {
    label: "✅ Abstract Reminder",
    emailSubject: "Upload your Driver's Abstract to stay verified",
    title: "Keep your verified badge active",
    body: "Your Driver's Abstract needs to be renewed annually. Upload a fresh copy (dated within 30 days) to maintain your verified status.",
    target: "driver",
  },
];

const PROVINCES = ["AB", "BC", "ON", "SK", "MB", "QC", "NS", "NB", "NL", "PEI", "NT", "NU", "YT"];

const fmtDate = (ts) => {
  if (!ts?.seconds) return "—";
  return new Date(ts.seconds * 1000).toLocaleString("en-CA", {
    timeZone: "America/Toronto",
    month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const STATUS_STYLE = {
  pending:    { color: "#EAB308", bg: "rgba(234,179,8,0.1)"   },
  processing: { color: "#3B82F6", bg: "rgba(59,130,246,0.1)"  },
  delivered:  { color: "#22C55E", bg: "rgba(34,197,94,0.1)"   },
  failed:     { color: "#EF4444", bg: "rgba(239,68,68,0.1)"   },
};

export default function Notifications() {
  // ── Compose state ─────────────────────────────────────────────────────────
  const [title,        setTitle]        = useState("");
  const [body,         setBody]         = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [offerCode,    setOfferCode]    = useState("");
  const [sending,      setSending]      = useState(false);
  const [sent,         setSent]         = useState(null); // { pushSent, emailSent, total }

  // ── Filters ───────────────────────────────────────────────────────────────
  const [filterRole,        setFilterRole]        = useState("all");
  const [filterPlan,        setFilterPlan]        = useState("all");
  const [filterStatus,      setFilterStatus]      = useState("active");
  const [filterProvince,    setFilterProvince]    = useState("");
  const [filterInactive,    setFilterInactive]    = useState("");   // days inactive
  const [filterNewSignups,  setFilterNewSignups]  = useState("");   // new in last N days
  const [specificUids,      setSpecificUids]      = useState("");   // comma-separated

  // ── History ───────────────────────────────────────────────────────────────
  const [history,      setHistory]      = useState([]);
  const [loadingHist,  setLoadingHist]  = useState(true);
  const [histPage,     setHistPage]     = useState(1);

  const loadHistory = async () => {
    setLoadingHist(true);
    try {
      const snap = await getDocs(
        query(collection(db, "adminBroadcasts"), orderBy("createdAt", "desc"), limit(100))
      );
      setHistory(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.warn("broadcast history:", e.message); }
    finally { setLoadingHist(false); }
  };
  useEffect(() => { loadHistory(); }, []);

  // ── Apply template ────────────────────────────────────────────────────────
  const applyTemplate = (t) => {
    setTitle(t.title);
    setBody(t.body);
    setEmailSubject(t.emailSubject);
    if (t.target !== "all") setFilterRole(t.target);
    if (t.offerCodePrompt) setOfferCode(""); // clear so admin fills in
  };

  // ── Build audience description ────────────────────────────────────────────
  const audienceDesc = () => {
    const parts = [];
    if (specificUids.trim()) {
      const count = specificUids.split(",").filter(Boolean).length;
      return `${count} specific user${count > 1 ? "s" : ""}`;
    }
    parts.push(filterRole === "all" ? "Everyone" : filterRole === "driver" ? "All Drivers" : "All Hirers");
    if (filterPlan !== "all") parts.push(`on ${filterPlan} plan`);
    if (filterStatus) parts.push(`(${filterStatus})`);
    if (filterProvince) parts.push(`in ${filterProvince}`);
    if (filterInactive) parts.push(`inactive ${filterInactive}+ days`);
    if (filterNewSignups) parts.push(`new in last ${filterNewSignups} days`);
    return parts.join(" · ");
  };

  // ── Send ──────────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!title.trim()) { alert("Enter a notification title."); return; }
    if (!body.trim())  { alert("Enter a notification message."); return; }

    const confirm = window.confirm(
      `Send to: ${audienceDesc()}\n\nTitle: ${title}\n\nMessage: ${body}${offerCode ? `\n\nOffer code: ${offerCode}` : ""}\n\nThis will send push notifications AND emails. Continue?`
    );
    if (!confirm) return;

    setSending(true);
    setSent(null);
    try {
      const filters = {
        role:         filterRole,
        planId:       filterPlan,
        accountStatus: filterStatus || null,
        province:     filterProvince || null,
        inactiveDays: filterInactive ? Number(filterInactive) : null,
        newSignupsDays: filterNewSignups ? Number(filterNewSignups) : null,
        specificUids:  specificUids.trim()
          ? specificUids.split(",").map(s => s.trim()).filter(Boolean)
          : [],
      };

      // Write broadcast doc — CF picks it up and delivers
      const ref = await addDoc(collection(db, "adminBroadcasts"), {
        title,
        body,
        emailSubject: emailSubject.trim() || title,
        offerCode:    offerCode.trim().toUpperCase() || null,
        filters,
        audienceDesc: audienceDesc(),
        status:       "pending",
        createdAt:    serverTimestamp(),
        createdBy:    "admin",
      });

      // Poll for delivery result (CF usually finishes in 5-30 seconds)
      setSent({ waiting: true });
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const snap = await getDocs(
            query(collection(db, "adminBroadcasts"), orderBy("createdAt", "desc"), limit(5))
          );
          const broadcast = snap.docs.find(d => d.id === ref.id)?.data();
          if (broadcast?.status === "delivered" || broadcast?.status === "failed") {
            clearInterval(poll);
            setSent(broadcast);
            setTitle(""); setBody(""); setEmailSubject(""); setOfferCode("");
            loadHistory();
          }
        } catch (_) {}
        if (attempts > 30) { // 30 seconds timeout
          clearInterval(poll);
          setSent({ timeout: true });
          loadHistory();
        }
      }, 1000);

    } catch (e) {
      alert("Failed to send. Please try again.");
      console.error(e);
      setSent(null);
    } finally {
      setSending(false);
    }
  };

  const inp = {
    width: "100%", background: "var(--surface2)", border: "1.5px solid var(--border)",
    borderRadius: 10, padding: "10px 14px", color: "var(--text)", fontSize: 13,
    outline: "none", fontFamily: "DM Sans", boxSizing: "border-box",
  };
  const label = (text) => (
    <label style={{ display: "block", color: "var(--text-muted)", fontSize: 11,
      fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
      {text}
    </label>
  );

  const totalPages = Math.ceil(history.length / PAGE_SIZE);
  const paged = history.slice((histPage - 1) * PAGE_SIZE, histPage * PAGE_SIZE);

  return (
    <div className="animate-fade">

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: "Syne", fontSize: 28, fontWeight: 800, color: "var(--text)", margin: 0 }}>
          Broadcast Notifications
        </h1>
        <p style={{ color: "var(--text-muted)", marginTop: 4, fontSize: 14 }}>
          Send push notifications + emails to drivers, hirers, or specific users
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 24, alignItems: "start" }}>

        {/* ── Compose panel ─────────────────────────────────────────────── */}
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 28 }}>
          <h2 style={{ fontFamily: "Syne", fontSize: 16, fontWeight: 800, color: "var(--text)", marginTop: 0, marginBottom: 20 }}>
            Compose Broadcast
          </h2>

          {/* Templates */}
          <div style={{ marginBottom: 20 }}>
            {label("Quick Templates")}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {TEMPLATES.map(t => (
                <button key={t.label} onClick={() => applyTemplate(t)}
                  style={{ background: "var(--surface2)", border: "1px solid var(--border)",
                    color: "var(--text-secondary)", borderRadius: 8, padding: "5px 11px",
                    fontSize: 12, cursor: "pointer" }}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div style={{ marginBottom: 14 }}>
            {label("Notification Title *")}
            <input value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Try Crewboard Pro — on us" style={inp} />
          </div>

          {/* Body */}
          <div style={{ marginBottom: 14 }}>
            {label("Message *")}
            <textarea value={body} onChange={e => setBody(e.target.value)}
              placeholder="Notification body text (shown in push + email)"
              style={{ ...inp, minHeight: 90, resize: "vertical" }} />
            <div style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 4 }}>{body.length} / 200 chars</div>
          </div>

          {/* Email subject */}
          <div style={{ marginBottom: 14 }}>
            {label("Email Subject Line (blank = use title)")}
            <input value={emailSubject} onChange={e => setEmailSubject(e.target.value)}
              placeholder="e.g. You're invited to try Crewboard Pro — free" style={inp} />
          </div>

          {/* Offer code */}
          <div style={{ marginBottom: 20 }}>
            {label("Attach Offer Code (optional)")}
            <input value={offerCode} onChange={e => setOfferCode(e.target.value.toUpperCase())}
              placeholder="e.g. LAUNCH2026 — shown in email and push data"
              style={{ ...inp, fontFamily: "monospace", letterSpacing: 1, fontWeight: 700 }} />
            {offerCode && (
              <div style={{ color: "#F97316", fontSize: 12, marginTop: 4, fontWeight: 600 }}>
                📧 Code will appear in a highlighted box in the email
              </div>
            )}
          </div>

          {/* ── Filters ──────────────────────────────────────────────────── */}
          <div style={{ background: "var(--surface2)", borderRadius: 12, padding: 16, marginBottom: 20 }}>
            <div style={{ color: "var(--text)", fontWeight: 700, fontSize: 14, marginBottom: 14 }}>
              🎯 Audience Filters
            </div>

            {/* Role */}
            <div style={{ marginBottom: 12 }}>
              {label("Role")}
              <div style={{ display: "flex", gap: 6 }}>
                {[["all", "👥 Everyone"], ["driver", "🚛 Drivers"], ["hirer", "🏢 Hirers"]].map(([v, l]) => (
                  <button key={v} onClick={() => setFilterRole(v)}
                    style={{ flex: 1, background: filterRole === v ? "#F97316" : "var(--surface)",
                      border: `1px solid ${filterRole === v ? "#F97316" : "var(--border)"}`,
                      color: filterRole === v ? "#fff" : "var(--text-secondary)",
                      borderRadius: 8, padding: "7px 6px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            {/* Plan — hirers only */}
            {(filterRole === "hirer" || filterRole === "all") && (
              <div style={{ marginBottom: 12 }}>
                {label("Hirer Plan")}
                <select value={filterPlan} onChange={e => setFilterPlan(e.target.value)} style={inp}>
                  <option value="all">All Plans</option>
                  <option value="basic">Basic Only</option>
                  <option value="pro_monthly">Pro Monthly</option>
                  <option value="pro_yearly">Pro Yearly</option>
                </select>
              </div>
            )}

            {/* Account status */}
            <div style={{ marginBottom: 12 }}>
              {label("Account Status")}
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={inp}>
                <option value="active">Active Only</option>
                <option value="suspended">Suspended</option>
                <option value="">All Statuses</option>
              </select>
            </div>

            {/* Province */}
            <div style={{ marginBottom: 12 }}>
              {label("Province (blank = all)")}
              <select value={filterProvince} onChange={e => setFilterProvince(e.target.value)} style={inp}>
                <option value="">All Provinces</option>
                {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>

            {/* Activity */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div>
                {label("Inactive for (days)")}
                <input type="number" min="1" value={filterInactive}
                  onChange={e => setFilterInactive(e.target.value)}
                  placeholder="e.g. 30" style={inp} />
              </div>
              <div>
                {label("New signups (last N days)")}
                <input type="number" min="1" value={filterNewSignups}
                  onChange={e => setFilterNewSignups(e.target.value)}
                  placeholder="e.g. 7" style={inp} />
              </div>
            </div>

            {/* Specific UIDs */}
            <div style={{ marginBottom: 4 }}>
              {label("Specific UIDs (overrides all other filters)")}
              <textarea value={specificUids} onChange={e => setSpecificUids(e.target.value)}
                placeholder="uid1, uid2, uid3 — paste from user table"
                style={{ ...inp, minHeight: 60, resize: "vertical", fontFamily: "monospace", fontSize: 11 }} />
            </div>
          </div>

          {/* Audience preview */}
          <div style={{ background: "rgba(249,115,22,0.07)", border: "1px solid rgba(249,115,22,0.2)",
            borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13 }}>
            <span style={{ color: "var(--text-muted)" }}>Audience: </span>
            <span style={{ color: "#F97316", fontWeight: 700 }}>{audienceDesc()}</span>
          </div>

          {/* Delivery note */}
          <div style={{ background: "rgba(59,130,246,0.07)", border: "1px solid rgba(59,130,246,0.2)",
            borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "var(--text-secondary)" }}>
            📱 <strong>Push</strong> sent to mobile users · 📧 <strong>Email</strong> sent via SendGrid · Delivered by Cloud Function
          </div>

          {/* Result */}
          {sent && !sent.waiting && !sent.timeout && (
            <div style={{ background: sent.status === "delivered" ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
              border: `1px solid ${sent.status === "delivered" ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`,
              borderRadius: 10, padding: "12px 16px", marginBottom: 16 }}>
              {sent.status === "delivered" ? (
                <div>
                  <div style={{ color: "#22C55E", fontWeight: 700, marginBottom: 4 }}>✅ Broadcast delivered!</div>
                  <div style={{ color: "var(--text-secondary)", fontSize: 13 }}>
                    {sent.totalUsers} users · {sent.pushSent} push · {sent.emailSent} emails · {sent.skipped} skipped
                  </div>
                </div>
              ) : (
                <div style={{ color: "#EF4444", fontWeight: 700 }}>❌ Broadcast failed: {sent.error}</div>
              )}
            </div>
          )}
          {sent?.waiting && (
            <div style={{ background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.3)",
              borderRadius: 10, padding: "12px 16px", marginBottom: 16, color: "#3B82F6", fontWeight: 600, fontSize: 13 }}>
              ⏳ Delivering… Cloud Function is sending push + emails
            </div>
          )}
          {sent?.timeout && (
            <div style={{ background: "rgba(234,179,8,0.1)", border: "1px solid rgba(234,179,8,0.3)",
              borderRadius: 10, padding: "12px 16px", marginBottom: 16, color: "#EAB308", fontWeight: 600, fontSize: 13 }}>
              ⏳ Still processing — check history for delivery status
            </div>
          )}

          <button onClick={handleSend} disabled={sending || !!sent?.waiting}
            style={{ width: "100%", background: "#F97316", border: "none", color: "#fff",
              borderRadius: 10, padding: 14, fontSize: 15, fontWeight: 700,
              cursor: (sending || sent?.waiting) ? "not-allowed" : "pointer",
              opacity: (sending || sent?.waiting) ? 0.6 : 1, fontFamily: "Syne" }}>
            {sending ? "Sending…" : sent?.waiting ? "⏳ Delivering…" : `🔔 Broadcast to ${audienceDesc()}`}
          </button>
        </div>

        {/* ── History panel ──────────────────────────────────────────────── */}
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 28 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h2 style={{ fontFamily: "Syne", fontSize: 16, fontWeight: 800, color: "var(--text)", margin: 0 }}>
              Broadcast History
            </h2>
            <button onClick={loadHistory}
              style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 8,
                padding: "6px 12px", color: "var(--text-secondary)", fontSize: 12, cursor: "pointer" }}>
              ↻ Refresh
            </button>
          </div>

          {loadingHist ? (
            <div style={{ color: "var(--text-muted)", textAlign: "center", padding: 40 }}>Loading…</div>
          ) : history.length === 0 ? (
            <div style={{ color: "var(--text-muted)", textAlign: "center", padding: 40 }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>🔔</div>
              No broadcasts sent yet
            </div>
          ) : (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {paged.map(b => {
                  const st = STATUS_STYLE[b.status] || STATUS_STYLE.pending;
                  return (
                    <div key={b.id} style={{ background: "var(--surface2)", borderRadius: 12, padding: 14 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                        <div style={{ color: "var(--text)", fontWeight: 700, fontSize: 13, flex: 1 }}>{b.title}</div>
                        <span style={{ background: st.bg, color: st.color, borderRadius: 20,
                          padding: "2px 8px", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                          {b.status}
                        </span>
                      </div>
                      <div style={{ color: "var(--text-secondary)", fontSize: 12, marginBottom: 6, lineHeight: 1.5 }}>
                        {b.body?.slice(0, 100)}{b.body?.length > 100 ? "…" : ""}
                      </div>
                      {b.offerCode && (
                        <div style={{ display: "inline-block", background: "rgba(249,115,22,0.1)",
                          border: "1px solid rgba(249,115,22,0.3)", borderRadius: 6,
                          padding: "2px 8px", fontSize: 11, color: "#F97316", fontFamily: "monospace",
                          fontWeight: 700, marginBottom: 6 }}>
                          🏷️ {b.offerCode}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 11, color: "var(--text-muted)" }}>
                        <span>👥 {b.audienceDesc}</span>
                        {b.status === "delivered" && (
                          <>
                            <span>📱 {b.pushSent} push</span>
                            <span>📧 {b.emailSent} emails</span>
                            {b.skipped > 0 && <span>⏭ {b.skipped} skipped</span>}
                          </>
                        )}
                        <span>🕐 {fmtDate(b.createdAt)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {history.length > PAGE_SIZE && (
                <Paginator page={histPage} totalPages={totalPages}
                  totalItems={history.length} pageSize={PAGE_SIZE} onChange={setHistPage} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}